import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  FormField,
  Input,
  PageHeader,
  SelectField,
} from "@jod/ui";
import { createFileRoute } from "@tanstack/react-router";
import type { FormEvent } from "react";
import { useState } from "react";
import {
  useCreateOperator,
  usePlatformOperators,
  useUpdateOperator,
} from "../../features/platform/queries.ts";
import type { PlatformOperator, PlatformRole } from "../../features/platform/types.ts";
import { getPlatformPrincipal } from "../../lib/auth-session.ts";

export const Route = createFileRoute("/_app/operators")({ component: OperatorsScreen });

function OperatorsScreen() {
  const isSuperAdmin = getPlatformPrincipal()?.role === "super_admin";
  const { data: operators, error } = usePlatformOperators(isSuperAdmin);
  const updateOperator = useUpdateOperator();

  if (!isSuperAdmin) {
    return (
      <p className="text-muted-foreground text-sm">
        Operator management requires the super_admin role.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Platform Operators"
        subtitle="Least-privilege internal accounts; deactivation revokes access immediately (FR-SADMIN-002)."
      />
      {error ? (
        <p role="alert" className="rounded-md bg-destructive/10 p-3 text-destructive text-sm">
          {error.message}
        </p>
      ) : null}
      <DataTable<PlatformOperator>
        rows={operators ?? []}
        getRowKey={(row) => row.id}
        empty={
          <p className="text-muted-foreground text-sm">
            No operator accounts yet — the root token (env) is the only access.
          </p>
        }
        columns={[
          {
            key: "operator",
            header: "Operator",
            cell: (row) => (
              <span className="font-medium">
                {row.displayName}
                <span className="block text-muted-foreground text-xs">{row.email}</span>
              </span>
            ),
          },
          { key: "role", header: "Role", cell: (row) => row.role },
          {
            key: "status",
            header: "Status",
            cell: (row) => (
              <Badge variant={row.isActive ? "default" : "destructive"}>
                {row.isActive ? "active" : "deactivated"}
              </Badge>
            ),
          },
          {
            key: "actions",
            header: "",
            cell: (row) => (
              <Button
                variant="outline"
                size="sm"
                disabled={updateOperator.isPending}
                onClick={() =>
                  updateOperator.mutate({
                    operatorId: row.id,
                    input: { isActive: !row.isActive },
                  })
                }
              >
                {row.isActive ? "Deactivate" : "Reactivate"}
              </Button>
            ),
          },
        ]}
      />
      <CreateOperatorCard />
    </div>
  );
}

function CreateOperatorCard() {
  const createOperator = useCreateOperator();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<PlatformRole>("support");
  const [issuedToken, setIssuedToken] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setIssuedToken(null);
    setErrorMessage(null);
    createOperator.mutate(
      { email, displayName, role },
      {
        onSuccess: (operator) => {
          setIssuedToken(operator.accessToken ?? null);
          setEmail("");
          setDisplayName("");
        },
        onError: (err) => setErrorMessage(err.message),
      },
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-display text-lg">Create operator</CardTitle>
        <p className="text-muted-foreground text-sm">
          The access token is shown exactly once below — hand it over securely.
        </p>
      </CardHeader>
      <CardContent>
        <form className="grid gap-4 sm:grid-cols-3" onSubmit={handleSubmit}>
          <FormField id="op-email" label="Email">
            <Input
              id="op-email"
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </FormField>
          <FormField id="op-name" label="Display name">
            <Input
              id="op-name"
              required
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
            />
          </FormField>
          <SelectField
            id="op-role"
            label="Role"
            value={role}
            onChange={(value) => setRole(value as PlatformRole)}
            options={[
              { value: "support", label: "Support" },
              { value: "super_admin", label: "Super admin" },
            ]}
          />
          <div className="flex items-end sm:col-span-3">
            <Button type="submit" disabled={createOperator.isPending}>
              {createOperator.isPending ? "Creating…" : "Create operator"}
            </Button>
          </div>
        </form>
        {issuedToken ? (
          <p className="mt-4 rounded-md bg-primary/10 p-3 font-mono text-sm">
            One-time access token: {issuedToken}
          </p>
        ) : null}
        {errorMessage ? (
          <p
            role="alert"
            className="mt-4 rounded-md bg-destructive/10 p-3 text-destructive text-sm"
          >
            {errorMessage}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
